import type { BlockRefEntity, CadEntity } from '../model/entity';
import type { Box } from '../model/geom';
import { mm } from '../model/units';
import {
  boxOf,
  facesAlong,
  gapBetween,
  mmOf,
  overlapArea,
  overlaps,
  unionOf,
  type Rule,
  type RuleContext,
  type RuleParams,
  type RuleSet,
  type Violation,
} from './engine';

/**
 * 표준 규칙 (작업지시서 P5-02 / P5-03 / P5-04)
 *
 * ── 기준값의 출처 ───────────────────────────────────────────────────────
 *
 *   주통로 2400 / 부통로 1800 / 피난통로 1200 / 휠체어 회전 1500 은
 *   국내 대형마트 사내 표준에서 통용되는 값이다. **법규 자체가 아니다.**
 *   프로젝트마다 다를 수 있으므로 전부 파라미터로 뺐다.
 *
 * ── 검사에서 의도적으로 보수적인 부분 ───────────────────────────────────
 *
 *   축 정렬 경계상자로 잰다. 45° 로 돌린 집기는 실제보다 크게 잡히므로
 *   "실제로는 통과인데 경고" 가 날 수 있다. 반대(놓치는 것)보다 낫다 —
 *   통로가 좁은 걸 못 잡으면 공사 후에 발견된다.
 */

const FIXTURE_LAYERS = new Set(['F-FIXTURE', 'F-REFRIG', 'F-POS', 'M-MD']);

/**
 * 쇼핑 통로를 만드는 집기 — **계산대는 뺀다.**
 *
 * ★ 계산대 레인은 900~1200mm 가 정상이다. 쇼핑 통로 기준(1800/2400)으로
 *   재면 정상 배치가 전부 오류로 뜨고, 그러면 사용자는 검증 패널을
 *   통째로 무시한다. 거짓 경보는 검사를 안 하느니만 못하다.
 *   계산대 간격은 블록의 clearance(측면 900)가 따로 관리한다.
 */
const AISLE_LAYERS = new Set(['F-FIXTURE', 'F-REFRIG', 'M-MD']);

/** 겹침·이격 검사 대상 (계산대 포함) */
function isFixture(e: CadEntity): boolean {
  return FIXTURE_LAYERS.has(e.layer);
}

/** 통로 폭 검사 대상 (계산대 제외) */
function isAisleFixture(e: CadEntity): boolean {
  return AISLE_LAYERS.has(e.layer);
}

function blockCodeOf(e: CadEntity): string | null {
  return e.type === 'blockref' ? (e as BlockRefEntity).blockCode.split('@')[0]! : null;
}

/* -------------------------------------------------------------------------- *
 * P5-02 통로 폭
 * -------------------------------------------------------------------------- */

export const AISLE_WIDTH: Rule = {
  id: 'aisle.width',
  name: '통로 폭',
  rationale:
    '마주 보는 매대 사이가 좁으면 카트가 교행하지 못합니다. 주통로 2400mm, 부통로 1800mm 가 사내 표준입니다. 좁은 통로는 매출과 안전에 함께 영향을 줍니다.',
  severity: 'error',
  enabled: true,

  check(ctx: RuleContext, params: RuleParams): Violation[] {
    const minMain = mm(params.mainMm ?? 2400);
    const minSub = mm(params.subMm ?? 1800);
    // 이보다 멀면 통로가 아니라 그냥 떨어져 있는 것이다
    const maxGap = mm(params.maxConsideredMm ?? 6000);

    const items = ctx.doc.entities
      .filter(isAisleFixture)
      .map((e) => ({ e, box: boxOf(e, ctx.blocks) }));

    const out: Violation[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i]!;
        const b = items[j]!;

        // 겹친 것은 간섭 규칙이 따로 잡는다 — 여기서 또 잡으면 시끄럽다
        if (overlaps(a.box, b.box)) continue;

        const gap = gapBetween(a.box, b.box);
        if (gap === 0 || gap > maxGap) continue;

        // 마주 보는 방향으로만 통로로 본다
        const facing = facesAlong(a.box, b.box, 'x') || facesAlong(a.box, b.box, 'y');
        if (!facing) continue;

        /*
         * 주통로/부통로 판정.
         * 마주 본 구간이 길수록 주동선일 가능성이 크다 — 매대 한 줄 전체가
         * 마주 보면 그건 주통로다. 짧게 스치는 건 부통로로 본다.
         */
        const span = facingSpan(a.box, b.box);
        const isMain = span >= mm(params.mainSpanMm ?? 6000);
        const required = isMain ? minMain : minSub;

        if (gap >= required) continue;

        const key = `${a.e.id}|${b.e.id}`;
        if (seen.has(key)) continue;
        seen.add(key);

        out.push({
          ruleId: 'aisle.width',
          severity: 'error',
          message: `${isMain ? '주통로' : '부통로'} 폭이 ${mmOf(gap)}mm 입니다. ${mmOf(required)}mm 이상이어야 합니다 (${mmOf(required - gap)}mm 부족).`,
          entityIds: [a.e.id, b.e.id],
          box: betweenBox(a.box, b.box),
          requiredMm: mmOf(required),
          actualMm: mmOf(gap),
        });
      }
    }

    return out;
  },
};

/** 마주 본 구간의 길이 (µm) */
function facingSpan(a: Box, b: Box): number {
  const overlapX = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const overlapY = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  return Math.max(overlapX, overlapY);
}

/** 두 집기 **사이**의 빈 공간. 위반을 강조할 때 여기를 칠한다. */
function betweenBox(a: Box, b: Box): Box {
  const gapX = Math.max(a.minX - b.maxX, b.minX - a.maxX);
  const gapY = Math.max(a.minY - b.maxY, b.minY - a.maxY);

  if (gapX > 0 && gapX >= gapY) {
    const left = a.maxX <= b.minX ? a : b;
    const right = left === a ? b : a;
    return {
      minX: left.maxX,
      maxX: right.minX,
      minY: Math.max(a.minY, b.minY),
      maxY: Math.min(a.maxY, b.maxY),
    };
  }
  if (gapY > 0) {
    const below = a.maxY <= b.minY ? a : b;
    const above = below === a ? b : a;
    return {
      minY: below.maxY,
      maxY: above.minY,
      minX: Math.max(a.minX, b.minX),
      maxX: Math.min(a.maxX, b.maxX),
    };
  }
  return unionOf(a, b);
}

/* -------------------------------------------------------------------------- *
 * P5-03 간섭·겹침
 * -------------------------------------------------------------------------- */

export const OVERLAP: Rule = {
  id: 'fixture.overlap',
  name: '집기 겹침',
  rationale:
    '두 집기가 같은 자리를 차지하고 있습니다. 도면에서는 보이지 않을 수 있지만 시공에서 반드시 문제가 됩니다.',
  severity: 'error',
  enabled: true,

  check(ctx: RuleContext, params: RuleParams): Violation[] {
    // 선이 살짝 스치는 것까지 잡으면 경고가 넘쳐 아무도 안 본다
    const minAreaUm2 = mm(params.ignoreBelowMm ?? 50) * mm(params.ignoreBelowMm ?? 50);

    const items = ctx.doc.entities
      .filter(isFixture)
      .map((e) => ({ e, box: boxOf(e, ctx.blocks) }));

    const out: Violation[] = [];

    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i]!;
        const b = items[j]!;
        const area = overlapArea(a.box, b.box);
        if (area <= minAreaUm2) continue;

        out.push({
          ruleId: 'fixture.overlap',
          severity: 'error',
          message: `집기 2개가 ${(area / 1e12).toFixed(2)}㎡ 겹쳐 있습니다.`,
          entityIds: [a.e.id, b.e.id],
          box: {
            minX: Math.max(a.box.minX, b.box.minX),
            minY: Math.max(a.box.minY, b.box.minY),
            maxX: Math.min(a.box.maxX, b.box.maxX),
            maxY: Math.min(a.box.maxY, b.box.maxY),
          },
        });
      }
    }

    return out;
  },
};

/* -------------------------------------------------------------------------- *
 * P5-04 소방·안전 이격
 * -------------------------------------------------------------------------- */

/** 앞을 막으면 안 되는 설비 */
const PROTECTED = new Set([
  'FIRE-EXT',
  'FIRE-HYDRANT',
  'EMERGENCY-EXIT',
  'AED',
  'ELEC-PANEL',
  'DOOR-SWING',
  'DOOR-SLIDE',
]);

export const SAFETY_CLEARANCE: Rule = {
  id: 'safety.clearance',
  name: '소방·안전 이격',
  rationale:
    '소화전·비상구·분전반 앞은 비워 두어야 합니다. 막혀 있으면 소방 검토에서 반려되고, 실제 화재 시 대피와 진화가 늦어집니다. 여기서 걸린 항목은 배치를 바꿔야 합니다.',
  severity: 'error',
  enabled: true,

  check(ctx: RuleContext): Violation[] {
    const out: Violation[] = [];

    const protectedItems = ctx.doc.entities.filter((e) => {
      const code = blockCodeOf(e);
      return code !== null && PROTECTED.has(code);
    });

    if (protectedItems.length === 0) return out;

    const blockers = ctx.doc.entities
      .filter(isFixture)
      .map((e) => ({ e, box: boxOf(e, ctx.blocks) }));

    for (const item of protectedItems) {
      const code = blockCodeOf(item)!;
      const clear = ctx.clearanceOf(code);
      const front = clear?.frontMm ?? 0;
      if (front <= 0) continue;

      const itemBox = boxOf(item, ctx.blocks);
      // 전면 방향을 모르므로 사방으로 넓힌다 — 보수적으로 잡는다.
      // 방향을 알면 좁힐 수 있지만, 놓치는 것보다 과하게 잡는 편이 낫다.
      const zone: Box = {
        minX: itemBox.minX - mm(front),
        minY: itemBox.minY - mm(front),
        maxX: itemBox.maxX + mm(front),
        maxY: itemBox.maxY + mm(front),
      };

      for (const blocker of blockers) {
        if (!overlaps(zone, blocker.box)) continue;

        const gap = gapBetween(itemBox, blocker.box);
        out.push({
          ruleId: 'safety.clearance',
          severity: 'error',
          message: `${nameOf(code)} 앞 ${front}mm 를 집기가 막고 있습니다 (현재 ${mmOf(gap)}mm).`,
          entityIds: [item.id, blocker.e.id],
          box: zone,
          requiredMm: front,
          actualMm: mmOf(gap),
        });
      }
    }

    return out;
  },
};

const KOREAN_NAME: Record<string, string> = {
  'FIRE-EXT': '소화기',
  'FIRE-HYDRANT': '옥내 소화전',
  'EMERGENCY-EXIT': '비상구',
  AED: '자동심장충격기',
  'ELEC-PANEL': '분전반',
  'DOOR-SWING': '여닫이문',
  'DOOR-SLIDE': '자동문',
};

function nameOf(code: string): string {
  return KOREAN_NAME[code] ?? code;
}

/* -------------------------------------------------------------------------- *
 * 휠체어 회전
 * -------------------------------------------------------------------------- */

export const WHEELCHAIR_TURN: Rule = {
  id: 'access.wheelchair',
  name: '휠체어 회전 반경',
  rationale:
    '통로 끝(막다른 곳)에서 휠체어가 돌려면 1500mm 가 필요합니다. 부족하면 되돌아 나와야 합니다.',
  severity: 'warning',
  enabled: true,

  check(ctx: RuleContext, params: RuleParams): Violation[] {
    const need = mm(params.turnMm ?? 1500);
    const items = ctx.doc.entities
      .filter(isAisleFixture)
      .map((e) => ({ e, box: boxOf(e, ctx.blocks) }));

    const out: Violation[] = [];

    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i]!;
        const b = items[j]!;
        if (overlaps(a.box, b.box)) continue;

        const gap = gapBetween(a.box, b.box);
        // 통로 폭 규칙이 이미 잡은 건 다시 안 잡는다
        if (gap === 0 || gap >= need || gap >= mm(params.subMm ?? 1800)) continue;

        out.push({
          ruleId: 'access.wheelchair',
          severity: 'warning',
          message: `통로 폭 ${mmOf(gap)}mm — 휠체어 회전(${mmOf(need)}mm)이 어렵습니다.`,
          entityIds: [a.e.id, b.e.id],
          box: betweenBox(a.box, b.box),
          requiredMm: mmOf(need),
          actualMm: mmOf(gap),
        });
      }
    }

    return out;
  },
};

/* -------------------------------------------------------------------------- */

export const STANDARD_RULES: Rule[] = [
  AISLE_WIDTH,
  OVERLAP,
  SAFETY_CLEARANCE,
  WHEELCHAIR_TURN,
];

/** 기본 규칙 세트 — 프로젝트가 따로 지정하지 않으면 이걸 쓴다 */
export const DEFAULT_RULE_SET: RuleSet = {
  id: 'default',
  name: '대형마트 표준',
  params: {
    'aisle.width': { mainMm: 2400, subMm: 1800, mainSpanMm: 6000, maxConsideredMm: 6000 },
    'access.wheelchair': { turnMm: 1500, subMm: 1800 },
    'fixture.overlap': { ignoreBelowMm: 50 },
  },
  disabled: [],
};
